/**
 * Plan Features Configuration
 * Defines what features are available for each subscription plan
 * This is the source of truth for all plan-related features in the app
 */

// Worker Plans
export const WORKER_PLANS = {
  free_worker: {
    name: 'Free',
    price: 0,
    // Core Features
    jobApplicationsLimit: 5, // per month
    searchVisibility: 'standard',
    searchRanking: 'basic',
    messaging: 'basic', // 10/day
    // Profile Features
    portfolioPhotos: 5,
    skillsListed: 5,
    featuredProfile: false,
    verifiedBadge: false,
    profileBoost: false,
    profileAnalytics: false,
    // Job Alerts
    jobAlerts: 'daily', // daily digest
    instantJobAlerts: false,
    // Tools
    contractTemplates: 0,
    invoiceGenerator: 'basic',
    timeTracking: 'basic',
    // Support
    support: 'email',
    prioritySupport: false,
    accountManager: false,
  },
  pro_worker: {
    name: 'Pro',
    price: 19.99,
    // Core Features
    jobApplicationsLimit: -1, // unlimited
    searchVisibility: '5x',
    searchRanking: 'top',
    messaging: 'priority',
    // Profile Features
    portfolioPhotos: -1,
    skillsListed: -1,
    featuredProfile: true,
    verifiedBadge: true,
    profileBoost: 3,
    profileAnalytics: true,
    // Job Alerts
    jobAlerts: 'instant_custom',
    instantJobAlerts: true,
    // Tools
    contractTemplates: -1,
    invoiceGenerator: 'full_branding',
    timeTracking: 'full_reports',
    // Support
    support: '24_7',
    prioritySupport: true,
    accountManager: true,
  },
  // LEGACY — kept so existing Elite subscribers retain features; not shown in UI
  elite_worker: {
    name: 'Elite',
    price: 49.99,
    // Core Features
    jobApplicationsLimit: -1, // unlimited
    searchVisibility: '5x',
    searchRanking: 'top',
    messaging: 'priority', // unlimited + priority
    // Profile Features
    portfolioPhotos: -1, // unlimited
    skillsListed: -1, // unlimited
    featuredProfile: true,
    verifiedBadge: true,
    profileBoost: 3, // 3x per week
    profileAnalytics: true,
    // Job Alerts
    jobAlerts: 'instant_custom',
    instantJobAlerts: true,
    // Tools
    contractTemplates: -1, // unlimited
    invoiceGenerator: 'full_branding',
    timeTracking: 'full_reports',
    // Support
    support: '24_7',
    prioritySupport: true,
    accountManager: true,
  }
};

// Contractor Plans
export const CONTRACTOR_PLANS = {
  starter_contractor: {
    name: 'Starter',
    price: 0,
    commission: 0, // 0% — platform never takes a cut of project payments
    // Job Posting
    jobPostingsLimit: 3, // per month
    jobVisibility: 'standard',
    featuredListings: 0,
    // Worker Search
    workerSearch: 'basic',
    candidateViews: 10, // per job
    advancedFiltering: false,
    aiMatching: false,
    // Team & Collaboration
    teamMembers: 1,
    teamCollaboration: false,
    // Communication
    messaging: 'basic', // 20/day
    bulkMessaging: false,
    // Tracking & Analytics
    applicantTracking: 'basic',
    hiringAnalytics: false,
    workerAnalytics: false,
    // Advanced Features
    apiAccess: false,
    customBranding: false,
    multiLocation: false,
    // Escrow & Templates
    escrowProtection: 'standard',
    projectTemplates: 0,
    // Support
    support: 'email',
    prioritySupport: false,
  },
  business_contractor: {
    name: 'Pro',
    price: 79,
    commission: 0,
    // Job Posting
    jobPostingsLimit: -1, // unlimited
    jobVisibility: '5x_featured',
    featuredListings: -1,
    // Worker Search
    workerSearch: 'advanced',
    candidateViews: -1, // unlimited
    advancedFiltering: true,
    aiMatching: true,
    // Team & Collaboration
    teamMembers: -1,
    teamCollaboration: true,
    // Communication
    messaging: 'unlimited',
    bulkMessaging: true,
    // Tracking & Analytics
    applicantTracking: 'full',
    hiringAnalytics: true,
    workerAnalytics: true,
    // Advanced Features
    apiAccess: true,
    customBranding: true,
    multiLocation: true,
    // Escrow & Templates
    escrowProtection: 'premium',
    projectTemplates: -1,
    // Support
    support: '24_7_dedicated',
    prioritySupport: true,
  },
  // LEGACY — kept so existing Enterprise subscribers retain features; not shown in UI
  enterprise_contractor: {
    name: 'Enterprise',
    price: 199,
    commission: 0,
    // Job Posting
    jobPostingsLimit: -1, // unlimited
    jobVisibility: '5x_featured',
    featuredListings: -1, // unlimited
    // Worker Search
    workerSearch: 'advanced',
    candidateViews: -1, // unlimited
    advancedFiltering: true,
    aiMatching: true,
    // Team & Collaboration
    teamMembers: -1, // unlimited
    teamCollaboration: true,
    // Communication
    messaging: 'priority', // unlimited + priority
    bulkMessaging: true,
    // Tracking & Analytics
    applicantTracking: 'full_custom',
    hiringAnalytics: true,
    workerAnalytics: true,
    // Advanced Features
    apiAccess: true,
    customBranding: true,
    multiLocation: true,
    // Escrow & Templates
    escrowProtection: 'premium',
    projectTemplates: -1, // unlimited
    // Support
    support: '24_7_dedicated',
    prioritySupport: true,
  }
};

/**
 * Get plan features for a user
 * @param {string} userType - 'worker' or 'contractor'
 * @param {string} planId - The subscription plan ID
 * @returns {Object} Plan features object
 */
export const getPlanFeatures = (userType, planId) => {
  if (userType === 'worker') {
    return WORKER_PLANS[planId] || WORKER_PLANS.free_worker;
  } else if (userType === 'contractor') {
    return CONTRACTOR_PLANS[planId] || CONTRACTOR_PLANS.starter_contractor;
  }
  return null;
};

/**
 * Check if a user has access to a specific feature
 * @param {string} userType - 'worker' or 'contractor'
 * @param {string} planId - The subscription plan ID
 * @param {string} feature - Feature name to check
 * @returns {boolean} Whether the user has access
 */
export const hasFeature = (userType, planId, feature) => {
  const planFeatures = getPlanFeatures(userType, planId);
  if (!planFeatures) return false;
  
  const value = planFeatures[feature];
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') return value !== 'basic' && value !== 'standard' && value !== 'email' && value !== 'daily';
  return false;
};

/**
 * Get feature limit value
 * @param {string} userType - 'worker' or 'contractor'
 * @param {string} planId - The subscription plan ID
 * @param {string} feature - Feature name
 * @returns {number} The limit value (-1 for unlimited, 0 for no access)
 */
export const getFeatureLimit = (userType, planId, feature) => {
  const planFeatures = getPlanFeatures(userType, planId);
  if (!planFeatures) return 0;
  return planFeatures[feature] || 0;
};

/**
 * Check if user needs to upgrade for a feature
 * @param {string} userType - 'worker' or 'contractor'
 * @param {string} currentPlan - Current plan ID
 * @param {string} feature - Feature needed
 * @returns {Object} { needsUpgrade: boolean, suggestedPlan: string }
 */
export const checkUpgradeNeeded = (userType, currentPlan, feature) => {
  if (hasFeature(userType, currentPlan, feature)) {
    return { needsUpgrade: false, suggestedPlan: null };
  }
  
  // Find the cheapest plan with this feature
  const plans = userType === 'worker' ? WORKER_PLANS : CONTRACTOR_PLANS;
  const planOrder = userType === 'worker' 
    ? ['free_worker', 'pro_worker']
    : ['starter_contractor', 'business_contractor'];
  
  for (const planId of planOrder) {
    if (hasFeature(userType, planId, feature)) {
      return { needsUpgrade: true, suggestedPlan: planId };
    }
  }
  
  return { needsUpgrade: true, suggestedPlan: null };
};

/**
 * Get display name for a plan
 * @param {string} planId - Plan ID
 * @returns {string} Display name
 */
export const getPlanDisplayName = (planId) => {
  const allPlans = { ...WORKER_PLANS, ...CONTRACTOR_PLANS };
  return allPlans[planId]?.name || 'Free';
};

/**
 * Format feature value for display
 * @param {any} value - The feature value
 * @param {string} featureType - Type of feature for formatting
 * @returns {string} Formatted display string
 */
export const formatFeatureValue = (value, featureType) => {
  if (value === -1) return 'Unlimited';
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  if (typeof value === 'number') return value.toString();
  
  // Handle string values
  const displayMap = {
    'standard': 'Standard',
    'basic': 'Basic',
    '3x': '3x Higher',
    '5x': '5x Higher',
    'priority': 'Priority',
    'unlimited': 'Unlimited',
    'email': 'Email only',
    'email_chat': 'Email & Chat',
    '24_7': '24/7 Priority',
    '24_7_dedicated': '24/7 Dedicated',
    'daily': 'Daily digest',
    'instant': 'Instant',
    'instant_custom': 'Instant + Custom',
    'full': 'Full features',
    'full_branding': 'Full + Branding',
    'full_reports': 'Full + Reports',
    'full_custom': 'Full + Custom',
    'enhanced': 'Enhanced',
    'premium': 'Premium + Insurance',
    'top': 'Top of results',
    'advanced': 'Advanced',
    '5x_featured': '5x Higher + Featured',
  };
  
  return displayMap[value] || value;
};
